/**
 * Guitar Effect Presets Library
 * Factory presets and preset management utilities
 */

// Factory preset definitions - optimized for quick loading
export const FACTORY_PRESETS = [
  {
    id: 'classic_rock',
    name: 'Classic Rock',
    category: 'rock',
    description: 'Marshall-style crunch with warm overdrive',
    icon: '🎸',
    color: 'orange',
    settings: {
      ampEnabled: true,
      ampModel: 'crunch',
      cabinet: '4x12',
      ampControls: { gain: 65, bass: 55, mid: 60, treble: 55, presence: 50, master: 65 },
      pedals: [
        { id: 'preset_od', type: 'overdrive', enabled: true, params: { drive: 40, tone: 55, level: 60 } }
      ]
    }
  },
  {
    id: 'metal_crush',
    name: 'Metal Crush',
    category: 'metal',
    description: 'High gain brutality with tight compression',
    icon: '🤘',
    color: 'red',
    settings: {
      ampEnabled: true,
      ampModel: 'metal',
      cabinet: '4x12v30',
      ampControls: { gain: 85, bass: 45, mid: 50, treble: 65, presence: 60, master: 60 },
      pedals: [
        { id: 'preset_comp', type: 'compressor', enabled: true, params: { threshold: 60, attack: 25, level: 70 } },
        { id: 'preset_dist', type: 'distortion', enabled: true, params: { drive: 75, tone: 60, level: 65 } },
        { id: 'preset_gate', type: 'noisegate', enabled: true, params: { threshold: 50, decay: 40 } }
      ]
    }
  },
  {
    id: 'clean_jazzy',
    name: 'Clean Jazzy',
    category: 'jazz',
    description: 'Warm clean tones with subtle modulation',
    icon: '🎷',
    color: 'cyan',
    settings: {
      ampEnabled: true,
      ampModel: 'clean',
      cabinet: '2x12',
      ampControls: { gain: 25, bass: 50, mid: 45, treble: 40, presence: 35, master: 70 },
      pedals: [
        { id: 'preset_chorus', type: 'chorus', enabled: true, params: { rate: 30, depth: 35, mix: 40 } }
      ]
    }
  },
  {
    id: 'blues_tone',
    name: 'Blues Tone',
    category: 'blues',
    description: 'Warm tube overdrive with soulful mids',
    icon: '🎤',
    color: 'blue',
    settings: {
      ampEnabled: true,
      ampModel: 'crunch',
      cabinet: '2x12',
      ampControls: { gain: 50, bass: 50, mid: 65, treble: 50, presence: 45, master: 65 },
      pedals: [
        { id: 'preset_od', type: 'overdrive', enabled: true, params: { drive: 55, tone: 50, level: 55 } }
      ]
    }
  },
  {
    id: 'funk_master',
    name: 'Funk Master',
    category: 'funk',
    description: 'Punchy clean with wah and compression',
    icon: '🕺',
    color: 'pink',
    settings: {
      ampEnabled: true,
      ampModel: 'clean',
      cabinet: '1x12',
      ampControls: { gain: 30, bass: 55, mid: 50, treble: 60, presence: 55, master: 70 },
      pedals: [
        { id: 'preset_comp', type: 'compressor', enabled: true, params: { threshold: 55, attack: 20, level: 75 } },
        { id: 'preset_wah', type: 'wah', enabled: true, params: { position: 50, range: 80, resonance: 65 } }
      ]
    }
  },
  {
    id: 'ambient_dream',
    name: 'Ambient Dream',
    category: 'ambient',
    description: 'Ethereal soundscapes with lush modulation',
    icon: '✨',
    color: 'violet',
    settings: {
      ampEnabled: true,
      ampModel: 'clean',
      cabinet: '2x12',
      ampControls: { gain: 20, bass: 45, mid: 40, treble: 50, presence: 40, master: 65 },
      pedals: [
        { id: 'preset_chorus', type: 'chorus', enabled: true, params: { rate: 25, depth: 45, mix: 50 } },
        { id: 'preset_phaser', type: 'phaser', enabled: true, params: { rate: 20, depth: 50, feedback: 45 } },
        { id: 'preset_tremolo', type: 'tremolo', enabled: true, params: { rate: 35, depth: 40, wave: 50 } }
      ]
    }
  },
  {
    id: 'country_twang',
    name: 'Country Twang',
    category: 'country',
    description: 'Bright clean with snappy compression',
    icon: '🤠',
    color: 'amber',
    settings: {
      ampEnabled: true,
      ampModel: 'clean',
      cabinet: '1x12',
      ampControls: { gain: 25, bass: 40, mid: 50, treble: 70, presence: 65, master: 70 },
      pedals: [
        { id: 'preset_comp', type: 'compressor', enabled: true, params: { threshold: 50, attack: 15, level: 70 } }
      ]
    }
  },
  {
    id: 'grunge_dirt',
    name: 'Grunge Dirt',
    category: 'rock',
    description: 'Raw fuzzy distortion with attitude',
    icon: '🔥',
    color: 'emerald',
    settings: {
      ampEnabled: true,
      ampModel: 'highGain',
      cabinet: '4x12',
      ampControls: { gain: 70, bass: 60, mid: 40, treble: 55, presence: 50, master: 60 },
      pedals: [
        { id: 'preset_fuzz', type: 'fuzz', enabled: true, params: { fuzz: 65, tone: 45, volume: 60 } }
      ]
    }
  },
  // NEW PRESETS USING NEW AMPS & EFFECTS
  {
    id: 'djent_machine',
    name: 'Djent Machine',
    category: 'metal',
    description: 'Tight, percussive modern metal tones',
    icon: '⚡',
    color: 'zinc',
    settings: {
      ampEnabled: true,
      ampModel: 'modernMetal',
      cabinet: '4x12v30',
      ampControls: { gain: 80, bass: 65, mid: 30, treble: 70, presence: 65, master: 50 },
      pedals: [
        { id: 'preset_gate', type: 'noisegate', enabled: true, params: { threshold: 55, decay: 35 } },
        { id: 'preset_comp', type: 'compressor', enabled: true, params: { threshold: 65, attack: 20, level: 75 } },
        { id: 'preset_eq', type: 'eqPedal', enabled: true, params: { low: 3, mid: -6, high: 4 } }
      ]
    }
  },
  {
    id: 'vintage_vibe',
    name: 'Vintage Vibe',
    category: 'rock',
    description: '60s British invasion tones',
    icon: '🌟',
    color: 'amber',
    settings: {
      ampEnabled: true,
      ampModel: 'britishStack',
      cabinet: '4x12',
      ampControls: { gain: 60, bass: 50, mid: 65, treble: 55, presence: 50, master: 55 },
      pedals: [
        { id: 'preset_trem', type: 'tremolo', enabled: true, params: { rate: 45, depth: 50, wave: 50 } },
        { id: 'preset_reverb', type: 'reverb', enabled: true, params: { decay: 40, predelay: 15, mix: 25 } }
      ]
    }
  },
  {
    id: 'smooth_jazz',
    name: 'Smooth Jazz',
    category: 'jazz',
    description: 'Warm, rounded jazz tones',
    icon: '🎹',
    color: 'yellow',
    settings: {
      ampEnabled: true,
      ampModel: 'jazzClean',
      cabinet: '2x12',
      ampControls: { gain: 25, bass: 60, mid: 55, treble: 40, presence: 35, master: 70 },
      pedals: [
        { id: 'preset_comp', type: 'compressor', enabled: true, params: { threshold: 45, attack: 40, level: 65 } },
        { id: 'preset_chorus', type: 'chorus', enabled: true, params: { rate: 25, depth: 30, mix: 35 } }
      ]
    }
  },
  {
    id: 'bb_king',
    name: 'BB King Style',
    category: 'blues',
    description: 'Singing sustain with touch sensitivity',
    icon: '👑',
    color: 'indigo',
    settings: {
      ampEnabled: true,
      ampModel: 'bluesBreaker',
      cabinet: '2x12',
      ampControls: { gain: 55, bass: 50, mid: 60, treble: 50, presence: 45, master: 60 },
      pedals: [
        { id: 'preset_od', type: 'overdrive', enabled: true, params: { drive: 45, tone: 50, level: 55 } },
        { id: 'preset_vib', type: 'vibrato', enabled: true, params: { rate: 35, depth: 25 } }
      ]
    }
  },
  {
    id: 'bass_beast',
    name: 'Bass Beast',
    category: 'rock',
    description: 'Thunderous bass guitar tone',
    icon: '🔊',
    color: 'purple',
    settings: {
      ampEnabled: true,
      ampModel: 'bassDriver',
      cabinet: '4x12',
      ampControls: { gain: 50, bass: 80, mid: 45, treble: 40, presence: 30, master: 70 },
      pedals: [
        { id: 'preset_comp', type: 'compressor', enabled: true, params: { threshold: 50, attack: 35, level: 75 } },
        { id: 'preset_oct', type: 'octaver', enabled: true, params: { octDown: 40, octUp: 0, dry: 80 } }
      ]
    }
  },
  {
    id: 'edge_delay',
    name: 'The Edge',
    category: 'rock',
    description: 'Rhythmic delay-driven soundscapes',
    icon: '🌊',
    color: 'sky',
    settings: {
      ampEnabled: true,
      ampModel: 'matchless',
      cabinet: '2x12',
      ampControls: { gain: 40, bass: 45, mid: 55, treble: 60, presence: 50, master: 55 },
      pedals: [
        { id: 'preset_delay', type: 'delay', enabled: true, params: { time: 50, feedback: 45, mix: 40 } },
        { id: 'preset_reverb', type: 'reverb', enabled: true, params: { decay: 55, predelay: 25, mix: 30 } }
      ]
    }
  },
  {
    id: 'srv_texas',
    name: 'Texas Flood',
    category: 'blues',
    description: 'Stevie Ray Vaughan inspired tone',
    icon: '🤠',
    color: 'emerald',
    settings: {
      ampEnabled: true,
      ampModel: 'vintageDeluxe',
      cabinet: '4x12',
      ampControls: { gain: 55, bass: 55, mid: 50, treble: 55, presence: 50, master: 60 },
      pedals: [
        { id: 'preset_od', type: 'overdrive', enabled: true, params: { drive: 60, tone: 55, level: 65 } },
        { id: 'preset_boost', type: 'booster', enabled: true, params: { boost: 40, tone: 50 } }
      ]
    }
  },
  {
    id: 'worship_ambient',
    name: 'Worship Ambient',
    category: 'ambient',
    description: 'Lush, atmospheric worship tones',
    icon: '✝️',
    color: 'violet',
    settings: {
      ampEnabled: true,
      ampModel: 'matchless',
      cabinet: '2x12',
      ampControls: { gain: 35, bass: 50, mid: 55, treble: 60, presence: 50, master: 55 },
      pedals: [
        { id: 'preset_comp', type: 'compressor', enabled: true, params: { threshold: 50, attack: 35, level: 70 } },
        { id: 'preset_delay', type: 'delay', enabled: true, params: { time: 55, feedback: 40, mix: 35 } },
        { id: 'preset_reverb', type: 'reverb', enabled: true, params: { decay: 70, predelay: 30, mix: 40 } }
      ]
    }
  },
  {
    id: 'rotary_leslie',
    name: 'Leslie Dreams',
    category: 'rock',
    description: 'Classic rotary speaker effect',
    icon: '🎡',
    color: 'orange',
    settings: {
      ampEnabled: true,
      ampModel: 'clean',
      cabinet: '2x12',
      ampControls: { gain: 30, bass: 50, mid: 50, treble: 55, presence: 45, master: 65 },
      pedals: [
        { id: 'preset_rotary', type: 'rotary', enabled: true, params: { speed: 55, drive: 25, mix: 70 } }
      ]
    }
  }
];

// Preset categories for filtering
export const PRESET_CATEGORIES = [
  { id: 'all', name: 'All', icon: '🎵' },
  { id: 'rock', name: 'Rock', icon: '🎸' },
  { id: 'metal', name: 'Metal', icon: '🤘' },
  { id: 'blues', name: 'Blues', icon: '🎤' },
  { id: 'jazz', name: 'Jazz', icon: '🎷' },
  { id: 'funk', name: 'Funk', icon: '🕺' },
  { id: 'country', name: 'Country', icon: '🤠' },
  { id: 'ambient', name: 'Ambient', icon: '✨' },
  { id: 'bass', name: 'Bass', icon: '🔊' },
];

// Get preset by ID
export const getPresetById = (id) => {
  return FACTORY_PRESETS.find(p => p.id === id) || null;
};

// Get presets by category
export const getPresetsByCategory = (category) => {
  if (category === 'all') return FACTORY_PRESETS;
  return FACTORY_PRESETS.filter(p => p.category === category);
};

// Convert preset settings to guitar effects format
export const applyPresetToTrack = (preset) => {
  if (!preset?.settings) return null;
  
  const { settings } = preset;
  return {
    enabled: true,
    ampEnabled: settings.ampEnabled,
    ampModel: settings.ampModel,
    cabinet: settings.cabinet,
    ampControls: { ...settings.ampControls },
    pedals: settings.pedals.map((p, i) => ({
      id: `${p.id}_${Date.now()}_${i}`,
      type: p.type,
      enabled: p.enabled,
      params: { ...p.params }
    }))
  };
};

// Create a user preset from current settings
export const createUserPreset = (name, guitarEffects, category = 'custom') => {
  return {
    id: `user_${Date.now()}`,
    name,
    category,
    description: 'Custom user preset',
    icon: '💾',
    color: 'slate',
    isUserPreset: true,
    settings: {
      ampEnabled: guitarEffects.ampEnabled,
      ampModel: guitarEffects.ampModel,
      cabinet: guitarEffects.cabinet || '4x12',
      ampControls: { ...guitarEffects.ampControls },
      pedals: (guitarEffects.pedals || []).map(p => ({
        id: p.id,
        type: p.type,
        enabled: p.enabled,
        params: { ...p.params }
      }))
    },
    createdAt: new Date().toISOString()
  };
};

// Validate preset structure
export const isValidPreset = (preset) => {
  return preset && 
    preset.id && 
    preset.name && 
    preset.settings &&
    typeof preset.settings.ampEnabled === 'boolean';
};

export default {
  FACTORY_PRESETS,
  PRESET_CATEGORIES,
  getPresetById,
  getPresetsByCategory,
  applyPresetToTrack,
  createUserPreset,
  isValidPreset
};
